import { BASE_CSS, TradeStyles } from '../styles/TradeStyles';
import { WorkspacePageStyles } from '@solar/workspace-ui/src/workspace/WorkspacePageStyles';

/**
 * The header chrome's stylesheet — the wide search bar and the department bar,
 * in Horizon's dark-ink chrome voice (redesign-2026-10-01).
 *
 * <p>It ships the same way every other rule in this theme does: a hoistable
 * `<style href precedence>` emitted by whichever component needs it, once per
 * document however many pull it in. The storefront's Tailwind build scans only
 * its own sources, so a utility class written here would never be generated,
 * and this theme's CSS has no business in the app's `globals.css` — a shop that
 * does not run this vertical must not carry its rules at all.
 *
 * <p>Everything is scoped under `ae-`, and both roots carry the `ae` class so
 * the scope variables in {@link BASE_CSS} resolve. The chrome's colours come
 * from the `--ae-chrome-*` scope that `BASE_CSS` declares on the shell's own
 * header and strip: the shell paints itself from inline `var(--color-*)` reads,
 * and `BASE_CSS`'s chrome bridge redefines those custom properties there, so
 * the header goes dark through the cascade rather than through a specificity
 * fight. The rules here that still carry `!important` are only the ones an
 * inline style forces (see each one's note); every colour travels by token.
 */
export const CHROME_CSS = `
${BASE_CSS}

/* ── The header search bar ─────────────────────────────────────────────────
 * A trade shop's search is not a utility icon beside the basket: it is how most
 * visits start, because the buyer arrived knowing a part number. In Horizon it
 * is the mockups' \`.search-box\`: a raised dark field on the ink chrome, amber
 * on focus, with the Search button fused to its right edge.
 */
.ae-search{
  display:flex;
  align-items:stretch;
  /* A preference, not a fixed size. \`.lms-header-controls-desktop\` is
     \`flex-shrink: 0\`, so the bar cannot be squeezed by the row running out of
     room — it has to yield on its own or it pushes the sign-in, account and
     basket controls off the right edge. */
  width:clamp(18rem,42vw,46rem);
  min-width:0;
}
/* The tight band. Between 1024px and 1280px the menu still draws its full text
   labels — the icon-only collapse does not start until 1023px — so the row is
   carrying a brand, four labelled menu items, this bar and three action
   controls at once, and it is the only stretch where they do not fit. The bar
   gives up the room because it is the one element that still works smaller.
   Below 860px of header-row width the whole cluster hides and the mobile
   drawer's own search takes over. */
@media (max-width:1280px){
  .ae-search{width:clamp(14rem,30vw,26rem)}
}
.ae-search .lms-search-inline{flex:1;min-width:0}
/* The suggestions dropdown stays a light panel — the same convention as the
   department menu's panel below: dark chrome, light panels. It sits inside the
   remapped header, so the tokens its inline styles read are restored here from
   the \`:root\` snapshot in BASE_CSS. The field itself re-enters the dark voice
   via the higher-specificity \`.sb-cmd-input\` rule after this one. */
.ae-search .lms-search-inline{
  --color-surface-card:var(--ae-page-surface);
  --color-surface-muted:var(--ae-page-raised);
  --color-text-primary:var(--ae-page-ink);
  --color-text-secondary:var(--ae-page-muted);
  --color-neutral-100:var(--ae-page-hover);
  --color-neutral-200:var(--ae-page-hairline);
  --color-border-subtle:var(--ae-page-line);
  --color-border-strong:var(--ae-page-line-strong);
  --color-border-focus:var(--ae-page-focus);
  --color-brand-500:var(--ae-page-brand);
  --color-brand-600:var(--ae-page-brand-deep);
  --color-text-on-brand:var(--ae-page-on-brand);
}
/* \`SbCommandInput\` writes its pill geometry as an INLINE style object, so the
   GEOMETRY below carries \`!important\` — a stylesheet cannot reach an inline
   literal any other way, and this is the whole of the patch layer. The
   COLOURS need none of it: every colour in the runtime's inline styles and in
   its own injected hover/focus \`!important\` rules is read through a
   \`var(--color-*)\`, so redefining those names on the wrapper recolours rest,
   hover (line-strong via \`--color-gray-500\`) and focus (amber via
   \`--color-brand-500\`, whose 28% ring reads as the mockups' sun-glow) with
   the runtime's own rules doing the painting. */
.ae-search .sb-cmd-input[data-sb-size="sm"]{
  --color-surface-card:var(--ae-chrome-field);
  --color-text-primary:var(--ae-chrome-ink);
  --color-text-secondary:var(--ae-chrome-muted);
  --color-border-subtle:var(--ae-chrome-line);
  --color-gray-500:var(--ae-chrome-line-strong);
  --color-brand-500:var(--ae-chrome-accent);
  /* Square, and square on the button side especially: the pair has to read as
     one control, and a pill's right cap cannot meet a button's left edge.
     Inline: \`borderRadius: var(--radius-pill, 9999px)\`. */
  border-radius:var(--ae-radius) 0 0 var(--ae-radius) !important;
  /* Inline: \`borderWidth: 1.5\` on all sides; the mockups' field is a 1px
     hairline, and the button supplies the right edge. */
  border-width:1px !important;
  border-right-width:0 !important;
  /* Inline: \`borderColor: color-mix(currentColor 32%, transparent)\`. */
  border-color:var(--ae-chrome-line) !important;
  /* Inline: \`boxShadow: var(--shadow-sm)\` — nothing floats on the chrome.
     Killed here rather than by redefining \`--shadow-sm\`, which is the
     platform's geometry token and not this skin's to rewrite. */
  box-shadow:none !important;
  /* Inline: \`padding: 6px 16px\`; the mockups' field is 0 12px with the
     height carrying the rhythm. */
  padding:0 .75rem !important;
  height:2.75rem;
}
/* The magnifier/spinner stroke is a presentation ATTRIBUTE whose var() value
   browsers drop (var() is invalid in presentation attributes), and any
   stylesheet rule outranks an attribute — no \`!important\` needed. */
.ae-search .sb-cmd-input[data-sb-size="sm"] svg{stroke:var(--ae-chrome-muted)}
.ae-search__go{
  display:grid;
  place-items:center;
  flex:0 0 auto;
  width:3.25rem;
  border:1px solid var(--ae-accent);
  border-radius:0 var(--ae-radius) var(--ae-radius) 0;
  background:var(--ae-accent);
  /* Amber cannot carry white, so the glyph is the theme's own on-accent ink.
     See the \`text.on-accent\` token. */
  color:var(--ae-on-accent);
  cursor:pointer;
  transition:background .15s ease,border-color .15s ease;
}
.ae-search__go:hover{
  background:var(--ae-accent-deep);
  border-color:var(--ae-accent-deep);
}

/* ── The department bar ────────────────────────────────────────────────────
 * The shell's category strip is a white band with a hairline under it, which is
 * right for the chip row it was built for and wrong for this bar: in Horizon
 * the departments are the second row of the dark chrome, one piece with the
 * header above them. So the strip wrapper hands its padding and its white
 * ground to the bar, which paints edge to edge in the chrome ink and sets its
 * own. The wrapper is styled INLINE by the runtime and has no class, so it is
 * matched by what it CONTAINS and its inline literals are the one thing only
 * \`!important\` can reach — these three declarations and the menu-row hide
 * below are the rest of the patch layer.
 */
div:has(> .ae-departments){
  padding:0 !important;
  border-bottom:0 !important;
  background:transparent !important;
}
/* The chrome ink, as approved in redesign-2026-10-01: the owner's earlier
   objection was to a block of BRAND COLOUR under the header, not to the dark
   voice — Horizon's chrome is the brand voice, and the hairline between the
   header row and this one is the header's own border, recoloured by the
   chrome bridge. The band itself ends clean on the page, as the mockups'
   \`.site-head\` does. */
.ae-departments{background:var(--ae-chrome);position:relative;z-index:20}
.ae-departments{--ae-focus:var(--ae-chrome-focus)}
.ae-departments__inner{position:relative;display:flex;align-items:center;max-width:var(--ae-max);margin-inline:auto;padding-inline:var(--ae-gutter);gap:16px}
.ae-departments__list{
  display:flex;
  flex:1;
  min-width:0;
  align-items:center;
  gap:16px;
  margin:0;
  padding:0;
  list-style:none;
  /* Every category remains available in the native All products disclosure. */
}
/* Lined up with the page, not the viewport edge: the band paints full width, its links
   sit in the same content column as the header row and the content. */
.ae-departments__list{max-width:var(--ae-max);margin-inline:auto}
.ae-departments__aside--first{margin-left:auto}
.ae-departments__link{
  display:flex;
  align-items:center;
  /* No wrap: a two-word department breaking mid-bar is what makes a nav row
     look improvised. */
  white-space:nowrap;
  padding:.75rem 0;
  min-height:46px;
  font-size:.9rem;
  font-weight:500;
  color:var(--ae-chrome-muted);
  text-decoration:none;
  transition:color .15s ease;
}
/* The hover mark is the mockups': the link brightens to the full inverse ink,
   nothing moves. */
.ae-departments__link:hover{color:var(--ae-chrome-ink)}
.ae-departments__link:focus-visible{
  outline:2px solid var(--ae-focus);
  outline-offset:-2px;
}
.ae-department-menu{flex:none}
.ae-department-menu summary{display:flex;align-items:center;gap:10px;min-height:50px;font-size:.875rem;font-weight:650;color:var(--ae-chrome-ink);list-style:none;cursor:pointer}
.ae-department-menu summary::-webkit-details-marker{display:none}
.ae-department-menu__chevron{margin-left:12px;transition:transform .15s}
.ae-department-menu[open] .ae-department-menu__chevron{transform:rotate(180deg)}
/* Dark chrome, light panel — the mockups' mega-menu convention, and the same
   one the search suggestions follow. The panel's own scope returns to the
   selling-surface focus voice. */
.ae-department-menu__panel{position:absolute;left:var(--ae-gutter);top:100%;width:min(700px,calc(100% - 2 * var(--ae-gutter)));max-height:70dvh;overflow:auto;padding:24px;border:1px solid var(--ae-border);border-radius:0 0 var(--ae-radius-lg) var(--ae-radius-lg);background:var(--ae-surface);box-shadow:var(--ae-shadow);z-index:30}
.ae-department-menu__panel{--ae-focus:var(--ae-page-focus)}
.ae-department-menu__panel > p{font-size:.75rem;font-weight:650;color:var(--ae-accent-ink);text-transform:uppercase;letter-spacing:.08em;margin:0 0 14px}
.ae-department-menu__panel ul{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));list-style:none;margin:0;padding:0;gap:6px 18px}
.ae-department-menu__panel li a{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:48px;padding:10px 0;font-size:.875rem;text-decoration:none;color:var(--ae-ink);border-bottom:1px solid var(--ae-border)}
.ae-department-menu__panel a:hover{color:var(--ae-link);text-decoration:underline;text-underline-offset:4px}
.ae-department-menu__panel li a span{color:var(--ae-muted)}
.ae-department-menu__help{display:flex;flex-wrap:wrap;gap:4px 20px;margin-top:20px;padding-top:12px;border-top:1px solid var(--ae-border)}
.ae-department-menu__help a{display:flex;align-items:center;min-height:44px;font-size:.8125rem;color:var(--ae-muted);text-decoration:none}
.ae-visually-hidden{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0}
.ae-mobile-search{display:none}
@media(max-width:1150px){.ae-departments__featured:nth-child(n+3){display:none}.ae-departments__inner{gap:12px}}
@media(max-width:860px){
  /* The drawer search is the mockups' field on the dark chrome: raised ink
     fill, hairline border, amber submit. */
  .ae-mobile-search{display:flex;margin:12px var(--ae-gutter) 4px;border:1px solid var(--ae-chrome-line);border-radius:var(--ae-radius);background:var(--ae-chrome-field);overflow:hidden}
  .ae-mobile-search input{flex:1;min-width:0;width:100%;border:0;background:transparent;padding:12px;font:inherit;font-size:16px;color:var(--ae-chrome-ink)}
  .ae-mobile-search input::placeholder{color:var(--ae-chrome-muted)}
  .ae-mobile-search button{display:grid;place-items:center;min-width:48px;min-height:48px;border:0;background:var(--ae-accent);color:var(--ae-on-accent);cursor:pointer}
  .ae-departments__featured{display:none}
  .ae-departments__link{font-size:.8125rem;padding:12px 0;min-height:48px}
  .ae-department-menu__panel ul{grid-template-columns:repeat(2,minmax(0,1fr))}
}
@media(max-width:480px){
  .ae-departments__inner{gap:6px}.ae-departments__aside:not(.ae-departments__aside--first){display:none}
  .ae-department-menu__panel{padding:16px}.ae-department-menu__panel ul{column-gap:16px}
}
@media(prefers-reduced-motion:reduce){.ae-department-menu__chevron{transition:none}}

/* One header row, as approved: logo, search, three actions. The operator's top-level menu
   moved onto the band above, so the header's own desktop menu row is hidden — only while
   the band is actually on the page, so a shop with no departments keeps its menu. The
   menu's markup stays: the mobile drawer is built from it. Its \`display\` is an inline
   style, so this is the last of the patch layer. */
body:has(.ae-departments) .lms-header-nav-desktop{display:none !important}

/* The trade VAT switch shares the strip with the bar. The sf-vat-strip class is the
   host CLASS, not this theme's — the shared shell block wraps the switch and
   this theme says what it looks like beside a department bar, which is the only
   place the two ever meet. In Horizon it is the chrome's last row, divided from
   the departments by the same hairline the mockups give \`.cats\`; the switch
   inside it recolours itself through the chrome bridge in BASE_CSS (muted
   labels, hairline pills, the pressed basis an amber fill carrying the
   on-accent ink — the mockups' \`.vat-switch\`). Scoped to the case where the
   wrapper actually gave its padding up, since on a shop with no departments
   the wrapper keeps its own and a second one here would double it. */
div:has(> .ae-departments) > .sf-vat-strip{
  background:var(--ae-chrome);
  border-top:1px solid var(--ae-chrome-line);
  padding:10px var(--ae-gutter);
}
`;

/** Emits {@link CHROME_CSS} once per document. */
export function ChromeStyles() {
  return <><TradeStyles id="chrome" css={CHROME_CSS} /><WorkspacePageStyles /></>;
}
